import {LocationGroupGranularityType} from './enum/LocationGroupGranularityType'
import {IAddressComponents, ILocationValue} from './types/ILocationPropertyTypeInterfaces'
import {IGroupFilter} from '../../../types/IGroupFIlter'
import {buildStringHasValueExpression} from '../../../functions/buildPropertyValueSortStages'

export type LocationBucketLevel = Exclude<LocationGroupGranularityType, LocationGroupGranularityType.Address>

export interface ILocationBucket {
  level: LocationBucketLevel
  value: string
}

export type ILocationBucketFullValue = Omit<ILocationValue, 'coordinates'> & {
  coordinates: null
}

export type ILocationIdentityKey = Pick<ILocationValue, 'address' | 'displayAddress'> & {
  coordinates: ILocationValue['coordinates'] | null
}

export type ILocationIdentityFullValue = ILocationIdentityKey & Pick<ILocationValue, 'addressComponents'>

export const LOCATION_IDENTITY_FIELDS: ReadonlyArray<keyof ILocationIdentityKey> = ['address', 'displayAddress', 'coordinates']

export const EMPTY_ADDRESS_COMPONENTS: IAddressComponents = {
  country: null,
  state: null,
  county: null,
  commune: null,
  city: null,
  street: null,
  postalCode: null,
}

export const LOCATION_COMPONENT_BY_GRANULARITY: Record<LocationBucketLevel, keyof IAddressComponents> = {
  [LocationGroupGranularityType.City]: 'city',
  [LocationGroupGranularityType.Country]: 'country',
}

export function isLocationBucketLevel(value: unknown): value is LocationBucketLevel {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(LOCATION_COMPONENT_BY_GRANULARITY, value)
}

export function isLocationBucket(value: unknown): value is ILocationBucket {
  if(!value || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }
  const bucket = value as Partial<ILocationBucket>
  return isLocationBucketLevel(bucket.level) && typeof bucket.value === 'string'
}

export function toLocationBucketKey(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

export function isLocationIdentityKey(value: unknown): value is ILocationIdentityKey {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value) && !isLocationBucket(value)
}

export function buildLocationComponentPath(valuePath: string, level: LocationBucketLevel): string {
  return `${valuePath}.addressComponents.${LOCATION_COMPONENT_BY_GRANULARITY[level]}`
}

export function buildLocationBucketFullValue(level: LocationBucketLevel, value: string): ILocationBucketFullValue {
  return {
    displayAddress: value,
    address: value,
    coordinates: null,
    addressComponents: {
      ...EMPTY_ADDRESS_COMPONENTS,
      [LOCATION_COMPONENT_BY_GRANULARITY[level]]: value,
    },
  }
}

export function buildLocationIdentityFullValue(key: ILocationIdentityKey): ILocationIdentityFullValue {
  return {
    address: key.address,
    displayAddress: key.displayAddress,
    coordinates: key.coordinates,
    addressComponents: {...EMPTY_ADDRESS_COMPONENTS},
  }
}

export function buildLocationIdentityExpression(valuePath: string): Record<string, unknown> {
  return {
    $cond: [
      {$eq: [{$type: `$${valuePath}`}, 'object']},
      Object.fromEntries(LOCATION_IDENTITY_FIELDS.map(field => [field, {$ifNull: [`$${valuePath}.${field}`, null]}])),
      null,
    ],
  }
}

export function buildLocationBucketExpression(valuePath: string, level: LocationBucketLevel): Record<string, unknown> {
  const componentPath = buildLocationComponentPath(valuePath, level)
  return {
    $cond: [
      buildStringHasValueExpression(componentPath),
      `$${componentPath}`,
      null,
    ],
  }
}

export function buildLocationBucketGroupFilter(valuePath: string, bucket: ILocationBucket): IGroupFilter {
  return {
    [buildLocationComponentPath(valuePath, bucket.level)]: bucket.value,
  }
}

export function buildEmptyLocationBucketGroupFilter(valuePath: string, level: LocationBucketLevel): IGroupFilter {
  const componentPath = buildLocationComponentPath(valuePath, level)
  return {
    $or: [
      {[componentPath]: {$exists: false}},
      {[componentPath]: {$in: [null, '']}},
    ],
  }
}
